import config from "../../config.json";
import { normalizeApiError } from "../errors/normalizeApiError";
const BASE_AUTH_API = config.BASE_AUTH_API;
// Outcome kinds of a `/me` verification (NA-036). Only REJECTED may end a
// session; every other failure leaves the session undecided and retryable.
export const ME_STATUS = Object.freeze({
    OK: "ok", // verified: `user` is the server's answer
    REJECTED: "rejected", // the server refused the token (expired/invalid/unknown user)
    NETWORK: "network", // no answer: offline, DNS, CORS, or the timeout below
    SERVER: "server", // the server answered with a 5xx / 408 / 429
    INVALID: "invalid", // 2xx or unexpected 4xx with a body we cannot use
});

export const ME_TIMEOUT_MS = 10000;

const isUsableUser = (user) =>
    Boolean(user) && typeof user === "object" && typeof user.role === "string" && user.role !== "";

/**
 * Verifies the stored token against `/me` and reports WHY it failed.
 * Never throws and never shows a dialog. 401 (missing or invalid token) and
 * 404 (the account no longer exists) mean the session is over.
 */
export const verifyCurrentUserAPI = async (token, { timeoutMs = ME_TIMEOUT_MS } = {}) => {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        let response;
        try {
            response = await fetch(`${BASE_AUTH_API}me`, {
                method: 'GET',
                headers: {
                    Accept: 'application/form-data',
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                signal: controller?.signal,
            });
        } catch {
            return { status: ME_STATUS.NETWORK };
        }

        // 401 (token not accepted) and 404 (the account no longer exists) end the session. 403 is a permission answer
        // about something else: it never signs anyone out.
        if ([401, 404].includes(response.status)) return { status: ME_STATUS.REJECTED };
        if (response.status >= 500 || response.status === 408 || response.status === 429) {
            return { status: ME_STATUS.SERVER };
        }

        let data;
        try {
            data = await response.json();
        } catch {
            return { status: controller?.signal.aborted ? ME_STATUS.NETWORK : ME_STATUS.INVALID };
        }
        if (response.ok && data?.type === "success" && isUsableUser(data.user)) {
            return { status: ME_STATUS.OK, user: data.user };
        }
        return { status: ME_STATUS.INVALID };
    } finally {
        if (timer) clearTimeout(timer);
    }
}

// Kept for callers that only want the user: resolves nothing on any failure.
// It no longer shows a blocking alert.
export const fetchCurrentUserAPI = async (token) => {
    const result = await verifyCurrentUserAPI(token);
    return result.status === ME_STATUS.OK ? result.user : undefined;
}
// Used only by components/users. Failures throw an Error carrying the HTTP
// status and the server's message, so the page can tell an expired session
// (401/403) from a rejected request (e.g. a duplicate email) instead of
// logging the admin out on every failure.
const userRequest = async (url, token, { method = "GET", body } = {}) => {
    const response = await fetch(url, {
        method,
        headers: {
            Accept: 'application/form-data',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    let responseData = {};
    try {
        responseData = await response.json();
    } catch {
        responseData = {};
    }
    if (!response.ok || !responseData.success) {
        const message =
            responseData.message || responseData.errors || responseData.error || "";
        const error = new Error(typeof message === "string" ? message : "");
        error.status = response.status;
        // Stable code and normalized error (utils/errors) for callers that translate or present the failure.
        error.code = typeof responseData.code === "string" ? responseData.code : undefined;
        error.apiError = normalizeApiError({ status: response.status, data: responseData });
        throw error;
    }
    return responseData;
};

export const fetchUsersAPI = async (token) => {
    const responseData = await userRequest(`${BASE_AUTH_API}users`, token);
    return responseData.users || [];
}
export const signupUserAPI = async (token, data) => {
    const responseData = await userRequest(`${BASE_AUTH_API}signup`, token, { method: 'POST', body: data });
    return responseData.token || true;
}
export const updateUserAPI = async (token, id, data) => {
    const responseData = await userRequest(`${BASE_AUTH_API}users/${id}`, token, { method: 'PUT', body: data });
    return responseData.updated || true;
}
// Global store access of one staff user. The server accepts exactly { globalAccess: boolean }, only from
// ceo / cto / developer, and records the change in the audit log.
export const setGlobalAccessAPI = async (token, id, globalAccess) => {
    const responseData = await userRequest(`${BASE_AUTH_API}users/${id}/global-access`, token, { method: 'PATCH', body: { globalAccess } });
    return responseData;
}
export const patchBetweenUserAPI = async (token, id, data) => {
    const responseData = await userRequest(`${BASE_AUTH_API}users/${id}/between`, token, { method: 'PATCH', body: data });
    return responseData.user || true;
}
